import React, { useState } from 'react';
import { Header } from '../../components/layout';
import { Button } from '../../components/common';
import { DownloadSimple, Truck, House, FileText, Calendar } from '@phosphor-icons/react';
import api from '../../services/api';
import './Reports.css';

// Downloads the manifest as a file, or surfaces the backend's
// "no orders found" message when there's nothing to generate.
const downloadManifest = async (endpoint, date, fallbackFilename) => {
  const response = await api.get(endpoint, { params: { date }, responseType: 'blob' });

  if (response.data.type && response.data.type.includes('json')) {
    const parsed = JSON.parse(await response.data.text());
    alert(parsed.message || 'No orders found for the selected date.');
    return;
  }

  const disposition = response.headers['content-disposition'];
  const match = disposition && disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;

  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export function Reports({ onMenuClick }) {
  const [pickupDate, setPickupDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [generatingPickup, setGeneratingPickup] = useState(false);
  const [generatingDelivery, setGeneratingDelivery] = useState(false);

  const handleGeneratePickup = async () => {
    if (!pickupDate) {
      alert('Please select a pickup date');
      return;
    }
    setGeneratingPickup(true);
    try {
      await downloadManifest('/api/reports/pickups', pickupDate, `pickups_manifest_${pickupDate}.pdf`);
    } catch (error) {
      alert('Failed to generate pickup manifest. Please try again.');
    } finally {
      setGeneratingPickup(false);
    }
  };

  const handleGenerateDelivery = async () => {
    if (!deliveryDate) {
      alert('Please select a delivery date');
      return;
    }
    setGeneratingDelivery(true);
    try {
      await downloadManifest('/api/reports/deliveries', deliveryDate, `delivery_package_${deliveryDate}.zip`);
    } catch (error) {
      alert('Failed to generate delivery manifest. Please try again.');
    } finally {
      setGeneratingDelivery(false);
    }
  };

  return (
    <>
      <Header
        title="Reports"
        subtitle="Generate and download operational reports"
        onMenuClick={onMenuClick}
      />
      <div className="page-content">
        <div className="reports-grid">
          <div className="report-card card">
            <div className="report-header">
              <div className="report-icon pickup">
                <Truck size={24} />
              </div>
              <div className="report-info">
                <h3>Pickup Manifest</h3>
                <p>Generate pickup schedule for delivery agents</p>
              </div>
            </div>
            <div className="report-body">
              <div className="report-form">
                <div className="form-group">
                  <label className="form-label">
                    <Calendar size={16} />
                    Select Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                  />
                </div>
                <Button
                  icon={<DownloadSimple size={18} />}
                  onClick={handleGeneratePickup}
                  disabled={!pickupDate}
                  loading={generatingPickup}
                  fullWidth
                >
                  Download Pickup PDF
                </Button>
              </div>
              {!pickupDate && (
                <div className="report-placeholder">
                  <FileText size={48} />
                  <p>Select a date to generate the pickup manifest</p>
                </div>
              )}
            </div>
          </div>

          <div className="report-card card">
            <div className="report-header">
              <div className="report-icon delivery">
                <House size={24} />
              </div>
              <div className="report-info">
                <h3>Delivery Manifest</h3>
                <p>Generate delivery schedule for delivery agents</p>
              </div>
            </div>
            <div className="report-body">
              <div className="report-form">
                <div className="form-group">
                  <label className="form-label">
                    <Calendar size={16} />
                    Select Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                  />
                </div>
                <Button
                  icon={<DownloadSimple size={18} />}
                  onClick={handleGenerateDelivery}
                  disabled={!deliveryDate}
                  loading={generatingDelivery}
                  fullWidth
                >
                  Download Delivery PDF
                </Button>
              </div>
              {!deliveryDate && (
                <div className="report-placeholder">
                  <FileText size={48} />
                  <p>Select a date to generate the delivery manifest</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="reports-info card">
          <h4>About Reports</h4>
          <ul>
            <li>
              <strong>Pickup Manifest:</strong> Contains list of all scheduled pickups for the selected date, including customer details, address, and time slots.
            </li>
            <li>
              <strong>Delivery Manifest:</strong> Contains list of all scheduled deliveries for the selected date, organized by route and time slot.
            </li>
            <li>
              Reports are generated in PDF format and can be printed directly for field agents.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default Reports;
